import React, { useState } from "react";
import { Dialog } from "../../ui/Dialog/Dialog";
import { Button } from "../../ui/Button/Button";
import { Input } from "../../ui/Input/Input";
import { MessageStrip } from "../../ui/MessageStrip/MessageStrip";
import { FormField } from "../../composite/FormField/FormField";
import { ImageUpload } from "../../composite/ImageUpload/ImageUpload";
import { customerApi } from "../../../services/customerService";

/**
 * CustomerCreateDialog - Modal for creating a new customer
 *
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close handler
 * @param {function} onSuccess - Success callback with created customer
 * @param {boolean} isRegularUser - If true, shows special messaging for regular users
 */
export function CustomerCreateDialog({
  open,
  onClose,
  onSuccess,
  isRegularUser = false,
}) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    contactEmail: "",
    contactPerson: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleImageChange = (file) => {
    setImageFile(file);
    if (errors.image) {
      setErrors((prev) => ({ ...prev, image: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Customer name is required";
    } else if (formData.name.length < 2 || formData.name.length > 255) {
      newErrors.name = "Name must be between 2 and 255 characters";
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = "Description must not exceed 1000 characters";
    }

    if (
      formData.contactEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)
    ) {
      newErrors.contactEmail = "Please enter a valid email address";
    }

    if (formData.contactPerson && formData.contactPerson.length > 255) {
      newErrors.contactPerson = "Contact person must not exceed 255 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setApiError("");

    try {
      // Step 1: Create the customer
      const response = await customerApi.createCustomer(formData);

      if (response.data.success) {
        const createdCustomer = response.data.data.customer;

        // Step 2: Upload image if provided
        if (imageFile) {
          try {
            const imageResponse = await customerApi.uploadCustomerImage(
              createdCustomer.id,
              imageFile
            );

            if (imageResponse.data.success) {
              // Update customer with image URL
              createdCustomer.imageUrl =
                imageResponse.data.data.customer.imageUrl;
            }
          } catch (imageError) {
            console.error("Error uploading image:", imageError);
            // Continue even if image upload fails
          }
        }

        // Reset form
        setFormData({
          name: "",
          description: "",
          contactEmail: "",
          contactPerson: "",
        });
        setImageFile(null);

        // Call success callback
        onSuccess?.(createdCustomer);
        onClose();
      } else {
        setApiError(response.data.message || "Failed to create customer");
      }
    } catch (error) {
      console.error("Error creating customer:", error);
      setApiError(
        error.response?.data?.message ||
          "An error occurred while creating the customer. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form on close
      setFormData({
        name: "",
        description: "",
        contactEmail: "",
        contactPerson: "",
      });
      setImageFile(null);
      setErrors({});
      setApiError("");
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      headerText={
        isRegularUser ? "Create Your Customer Profile" : "Create New Customer"
      }
      onAfterClose={handleClose}
      footer={
        <div className="flex justify-end gap-2 p-4">
          <Button design="Transparent" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button design="Emphasized" onClick={handleSubmit} disabled={loading}>
            {loading ? "Creating..." : "Create Customer"}
          </Button>
        </div>
      }
    >
      <div className="p-6 space-y-4">
        {apiError && (
          <MessageStrip design="Negative" className="mb-4">
            {apiError}
          </MessageStrip>
        )}

        {isRegularUser && (
          <MessageStrip design="Information" className="mb-4">
            Create your customer profile to get started. You can manage your
            services and track changes.
          </MessageStrip>
        )}

        {/* Profile Image Upload */}
        <ImageUpload
          label="Profile Image (Optional)"
          value={imageFile}
          onChange={handleImageChange}
          error={errors.image}
        />

        <FormField label="Customer Name" required error={errors.name}>
          <Input
            value={formData.name}
            onInput={(e) => handleChange("name", e.target.value)}
            placeholder="Enter customer name"
            valueState={errors.name ? "Error" : "None"}
          />
        </FormField>

        <FormField
          label="Description"
          error={errors.description}
          helpText="Optional description of the customer"
        >
          <Input
            value={formData.description}
            onInput={(e) => handleChange("description", e.target.value)}
            placeholder="Enter description (optional)"
            valueState={errors.description ? "Error" : "None"}
          />
        </FormField>

        <FormField label="Contact Email" error={errors.contactEmail}>
          <Input
            type="Email"
            value={formData.contactEmail}
            onInput={(e) => handleChange("contactEmail", e.target.value)}
            placeholder="contact@example.com"
            valueState={errors.contactEmail ? "Error" : "None"}
          />
        </FormField>

        <FormField label="Contact Person" error={errors.contactPerson}>
          <Input
            value={formData.contactPerson}
            onInput={(e) => handleChange("contactPerson", e.target.value)}
            placeholder="John Doe"
            valueState={errors.contactPerson ? "Error" : "None"}
          />
        </FormField>
      </div>
    </Dialog>
  );
}
